import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { from, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type { Recognizer, SpeechRecognitionCanceledEventArgs, SpeechRecognitionEventArgs, SpeechRecognizer } from 'microsoft-cognitiveservices-speech-sdk';
import { BASE_SPEECH_LOCALE } from './speech-locales';

const TARGET_SAMPLE_RATE = 16000;

// Azure Speech tokens are valid ~10 min. Refresh a bit early so a cached token never
// expires mid-handshake. The token is service-level (not per-user), so caching it client-side
// is safe and simply hides the function cold-start + STS hop behind the user's intent.
const TOKEN_TTL_MS = 8 * 60 * 1000;

interface SpeechTokenResponse {
	token: string;
	region: string;
}

interface CachedToken extends SpeechTokenResponse {
	fetchedAt: number;
}

/**
 * A running live-transcription session backed by the Azure Speech SDK.
 * `interim$` emits the in-progress hypothesis for the current utterance (replaced as you speak),
 * `finalized$` emits each finalized segment once, and `error$` emits a user-facing message on failure.
 */
export interface LiveTranscriptionSession {
	readonly connected$: Observable<void>;
	readonly interim$: Observable<string>;
	readonly finalized$: Observable<string>;
	readonly error$: Observable<string>;
	stop(): Promise<void>;
}

@Injectable({
	providedIn: 'root'
})
export class AudioTranscribeService {
	private readonly _http = inject(HttpClient);
	private readonly _config = inject<IAppSettings>(APP_CONFIG);
	private readonly _zone = inject(NgZone);

	private get _apimBase(): string {
		return this._config.api['apim'] ?? 'https://mylanguagebird-staging-apim.azure-api.net/';
	}

	private get _transcribeUrl(): string {
		return `${this._apimBase}azure/speech/transcribe`;
	}

	private get _tokenUrl(): string {
		return `${this._apimBase}azure/speech/token`;
	}

	private _cachedToken: CachedToken | null = null;
	private _tokenInFlight: Observable<SpeechTokenResponse> | null = null;

	private readonly _isRecording = signal(false);

	/** True while any recorder instance holds the single global recording slot. */
	readonly isRecording = this._isRecording.asReadonly();

	/**
	 * Claims the single global recording slot. Returns false if a recording is already
	 * active anywhere in the app. Called synchronously at the start of a recording so two
	 * near-simultaneous mic clicks can't both proceed.
	 */
	acquireRecordingLock(): boolean {
		if (this._isRecording()) {
			return false;
		}
		this._isRecording.set(true);
		return true;
	}

	/** Releases the global recording slot. Safe to call when not held. */
	releaseRecordingLock(): void {
		this._isRecording.set(false);
	}

	/**
	 * Warms the token ahead of recording so the cold-start + STS hop happen during idle time
	 * rather than on the mic click. Safe to call repeatedly (e.g. on hover/focus) — it no-ops
	 * while a token is fresh or a fetch is already in flight. Fire-and-forget.
	 */
	prewarm(): void {
		this._getToken().subscribe({ error: () => undefined });
	}

	/**
	 * Returns a cached token while it's fresh, otherwise fetches a new one. Concurrent callers
	 * share a single in-flight request so a burst of calls only hits the endpoint once.
	 */
	private _getToken(): Observable<SpeechTokenResponse> {
		const cached = this._cachedToken;
		if (cached && Date.now() - cached.fetchedAt < TOKEN_TTL_MS) {
			return of({ token: cached.token, region: cached.region });
		}

		if (this._tokenInFlight) {
			return this._tokenInFlight;
		}

		this._tokenInFlight = this._http.get<SpeechTokenResponse>(this._tokenUrl).pipe(
			tap({
				next: ({ token, region }) => {
					this._cachedToken = { token, region, fetchedAt: Date.now() };
					this._tokenInFlight = null;
				},
				error: () => {
					this._tokenInFlight = null;
				}
			}),
			shareReplay(1)
		);
		return this._tokenInFlight;
	}

	/**
	 * Opens a live transcription session that streams microphone audio directly to Azure Speech
	 * via WebSocket using a short-lived authorization token (the subscription key never reaches the
	 * browser). The Speech SDK is lazy-loaded so it stays out of the main bundle.
	 *
	 * Pass more than one locale to enable continuous language identification, which recognizes each
	 * utterance in whichever of the candidates was spoken. Azure allows only one locale per base
	 * language in that list — see `getRecognitionLocales`.
	 *
	 * Resolves once recognition has started; reject/`error$` cover token, mic, and connection failures.
	 */
	startLiveTranscription(languages: string | string[] = BASE_SPEECH_LOCALE): Observable<LiveTranscriptionSession> {
		const locales = (Array.isArray(languages) ? languages : [languages]).filter(Boolean);
		const candidates = locales.length ? locales : [BASE_SPEECH_LOCALE];

		return this._getToken().pipe(
			switchMap(({ token, region }) => from(this._createSession(token, region, candidates)))
		);
	}

	private async _createSession(token: string, region: string, locales: string[]): Promise<LiveTranscriptionSession> {
		// esbuild can't statically detect named exports on this CommonJS module, so the dynamic
		// import lands everything under `.default`. Unwrap it so SpeechConfig/AudioStreamFormat/etc.
		// are reachable as top-level names (otherwise they come back undefined at runtime).
		const sdkModule = await import('microsoft-cognitiveservices-speech-sdk');
		const SpeechSDK = (sdkModule as unknown as { default?: typeof sdkModule }).default ?? sdkModule;

		const connected$ = new ReplaySubject<void>(1);
		const interim$ = new Subject<string>();
		const finalized$ = new Subject<string>();
		const error$ = new Subject<string>();

		// Run all SDK operations outside Angular's zone to prevent Zone.js from wrapping
		// every WebSocket onmessage in change detection, which delays first recognition results.
		const session = await this._zone.runOutsideAngular(async () => {
			// Open the microphone NOW — before startContinuousRecognitionAsync establishes the
			// WebSocket — so audio is already buffering in the push stream during connection setup.
			// Without this, words spoken in the first 1-3 seconds after "Listening..." are lost
			// because fromDefaultMicrophoneInput() only starts capturing when the WS is ready.
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 }
			});

			const audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
			// Use the actual sample rate in case the browser can't honour the requested 16 kHz.
			const actualRate = audioCtx.sampleRate;
			const format = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(actualRate, 16, 1);
			const pushStream = SpeechSDK.AudioInputStream.createPushStream(format);

			// Pipe mic → push stream. ScriptProcessorNode is deprecated but universally supported;
			// AudioWorklet requires a separate worker file that can't be inlined in the bundle.
			const source = audioCtx.createMediaStreamSource(mediaStream);
			const processor = audioCtx.createScriptProcessor(2048, 1, 1);
			processor.onaudioprocess = (e) => {
				const float32 = e.inputBuffer.getChannelData(0);
				const int16 = new Int16Array(float32.length);
				for (let i = 0; i < float32.length; i++) {
					const s = Math.max(-1, Math.min(1, float32[i]));
					int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
				}
				pushStream.write(int16.buffer);
			};
			source.connect(processor);
			// Route through a silent destination so the graph stays alive without echo.
			const silentDest = audioCtx.createMediaStreamDestination();
			processor.connect(silentDest);

			const cleanup = () => {
				processor.disconnect();
				source.disconnect();
				void audioCtx.close();
				pushStream.close();
				mediaStream.getTracks().forEach((t) => t.stop());
			};

			const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
			// Raise segmentation silence threshold (default ~500 ms) so mid-sentence pauses
			// don't prematurely finalize an utterance and lose words at the boundary.
			speechConfig.setProperty(SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs, '2000');

			const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);

			let recognizer: SpeechRecognizer;
			if (locales.length > 1) {
				// Continuous language identification, so an instructor can move between English and the
				// language they teach within one recording and each utterance is recognized in whichever
				// was actually spoken. Continuous mode also sets RecognitionEndpointVersion to 2, which
				// routes the SDK to the universal v2 endpoint — the only one supporting mid-stream
				// switching. Note it detects per utterance, not per word: English words dropped into a
				// Spanish sentence stay in the Spanish transcription.
				const autoDetectConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(locales);
				autoDetectConfig.mode = SpeechSDK.LanguageIdMode.Continuous;
				recognizer = SpeechSDK.SpeechRecognizer.FromConfig(speechConfig, autoDetectConfig, audioConfig);
			} else {
				speechConfig.speechRecognitionLanguage = locales[0];
				recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
			}

			recognizer.sessionStarted = () => {
				connected$.next();
				connected$.complete();
			};

			recognizer.recognizing = (_s: Recognizer, e: SpeechRecognitionEventArgs) => {
				if (e.result.text) {
					interim$.next(e.result.text);
				}
			};

			recognizer.recognized = (_s: Recognizer, e: SpeechRecognitionEventArgs) => {
				if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech && e.result.text) {
					finalized$.next(e.result.text);
				}
			};

			recognizer.canceled = (_s: Recognizer, e: SpeechRecognitionCanceledEventArgs) => {
				if (e.reason === SpeechSDK.CancellationReason.Error) {
					error$.next('Transcription stopped unexpectedly. Please try again.');
				}
			};

			const complete = () => {
				connected$.complete();
				interim$.complete();
				finalized$.complete();
				error$.complete();
			};

			await new Promise<void>((resolve, reject) => {
				recognizer.startContinuousRecognitionAsync(
					() => resolve(),
					(err: string) => {
						console.error('[AudioTranscribe] startContinuousRecognitionAsync error:', err);
						cleanup();
						reject(new Error(typeof err === 'string' ? err : 'Failed to start transcription.'));
					}
				);
			});

			return {
				stop: () =>
					new Promise<void>((resolve) => {
						recognizer.stopContinuousRecognitionAsync(
							() => {
								recognizer.close();
								cleanup();
								complete();
								resolve();
							},
							() => {
								recognizer.close();
								cleanup();
								complete();
								resolve();
							}
						);
					})
			};
		});

		return {
			connected$: connected$.asObservable(),
			interim$: interim$.asObservable(),
			finalized$: finalized$.asObservable(),
			error$: error$.asObservable(),
			stop: session.stop
		};
	}

	/**
	 * Batch fallback: sends a recorded audio blob to the transcription endpoint.
	 * Decodes the browser audio (any format), resamples to 16 kHz mono PCM WAV,
	 * base64-encodes it, and posts JSON to APIM: {apim}/azure/speech/transcribe
	 */
	transcribe(audioBlob: Blob, language = BASE_SPEECH_LOCALE): Observable<string> {
		return from(this._blobToWavBase64(audioBlob)).pipe(
			switchMap((audioBase64) => this._http.post<{ text: string }>(this._transcribeUrl, { audioBase64, language })),
			map((response) => response.text)
		);
	}

	private async _blobToWavBase64(blob: Blob): Promise<string> {
		const arrayBuffer = await blob.arrayBuffer();
		const audioCtx = new AudioContext();
		const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
		await audioCtx.close();

		const numFrames = Math.ceil(audioBuffer.duration * TARGET_SAMPLE_RATE);
		const offlineCtx = new OfflineAudioContext(1, numFrames, TARGET_SAMPLE_RATE);
		const source = offlineCtx.createBufferSource();
		source.buffer = audioBuffer;
		source.connect(offlineCtx.destination);
		source.start(0);
		const resampled = await offlineCtx.startRendering();

		const pcm = resampled.getChannelData(0);
		const wavBuffer = this._encodeWav(pcm, TARGET_SAMPLE_RATE);

		const bytes = new Uint8Array(wavBuffer);
		let binary = '';
		bytes.forEach((b) => (binary += String.fromCharCode(b)));
		return window.btoa(binary);
	}

	private _encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
		const buffer = new ArrayBuffer(44 + samples.length * 2);
		const view = new DataView(buffer);

		const writeStr = (offset: number, str: string) => str.split('').forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));

		writeStr(0, 'RIFF');
		view.setUint32(4, 36 + samples.length * 2, true);
		writeStr(8, 'WAVE');
		writeStr(12, 'fmt ');
		view.setUint32(16, 16, true); // subchunk size
		view.setUint16(20, 1, true); // PCM
		view.setUint16(22, 1, true); // mono
		view.setUint32(24, sampleRate, true);
		view.setUint32(28, sampleRate * 2, true); // byte rate
		view.setUint16(32, 2, true); // block align
		view.setUint16(34, 16, true); // bits per sample
		writeStr(36, 'data');
		view.setUint32(40, samples.length * 2, true);

		let offset = 44;
		for (let i = 0; i < samples.length; i++) {
			const s = Math.max(-1, Math.min(1, samples[i]));
			view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
			offset += 2;
		}

		return buffer;
	}
}
